import React, { MouseEvent, useState } from 'react';

import AlertDialog from './AlertDialog';
import AuthService from '../../shared/services/AuthService';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import { COMPLETION_OPTIONS } from '../../shared/constants';
import CancelIcon from '@mui/icons-material/Cancel';
import CertIcon from '../../dashboard/components/CertIcon';
import CertService from '../../report/services/CertService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { CompletionModel } from '../../shared/models/CompletionModel';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import ErrorIcon from '@mui/icons-material/Error';
import FileService from '../../shared/services/FileService';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import LessonService from '../../dashboard/services/LessonService';
import Overlay from './Overlay';
import ProgressStatusCode from '../../dashboard/models/ProgressStatusCode';
import Separator from '../../content/images/separator.svg';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import styles from '../styles/CompletionMessage.module.css';
import { useExternalLessonContext } from '../../dashboard/models/ExternalLessonContext';
import { useLessonPlayerContext } from '../../dashboard/models/LessonPlayerContext';

interface CompletionMessageProps {
  completionStatus: CompletionModel;
  isCompleted?: boolean;
  userPoints: number;
  assignedPoints: number;
  status: ProgressStatusCode;
  hasCert: boolean;
  showContinue: boolean;
  handleReturnToLesson: () => void;
  handleReturnToCourse: () => void;
  handleContinue: () => void;
}

const ICON = {
  checkCircle: CheckCircleIcon,
  error: ErrorIcon,
  cancel: CancelIcon
};

export const CustomBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  '.MuiBreadcrumbs-separator': {
    [theme.breakpoints.up('sm')]: {
      transform: 'rotate(90deg)'
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  '.MuiBreadcrumbs-ol': {
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  }
}));

export default function CompletionMessage(props: CompletionMessageProps) {
  const {
    completionStatus,
    isCompleted,
    userPoints,
    assignedPoints,
    status,
    hasCert,
    showContinue
  } = props;

  const [fetchingCert, setFetchingCert] = useState(false);
  const [fetchCertError, setFetchCertError] = useState(false);

  const { lessonState } = useLessonPlayerContext();
  const { isExternalLesson } = useExternalLessonContext();

  const IconComponent = ICON[completionStatus.icon];
  const numOfpercentage =
    assignedPoints > 0 ? Math.round((userPoints / assignedPoints) * 100) : 0;

  const handleCertIconClick = (
    event: MouseEvent<HTMLDivElement> | MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    const lessonkey = lessonState?.answerParams.lessonkey;
    setFetchingCert(true);
    const promise = isNaN(+lessonkey)
      ? CertService.getCertFile(AuthService.Username, `${lessonkey}`)
      : CertService.getModuleCertificate(AuthService.Userkey, +lessonkey);
    promise
      .then((data) => {
        setFetchingCert(false);
        FileService.open(data);
      })
      .catch(() => {
        setFetchingCert(false);
        setFetchCertError(true);
      });
  };

  return (
    <Dialog open fullScreen onClose={props.handleReturnToLesson}>
      <DialogContent className={styles.container}>
        <Grid container className={styles.header}>
          <IconButton onClick={props.handleReturnToLesson}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid container className={styles.content}>
          <Grid>
            {status === 'P' && hasCert ? (
              <Grid container direction="column" alignItems="center">
                <Grid>
                  <CertIcon
                    status={LessonService.toCertStatus(status, hasCert)}
                    style={{ width: '45px' }}
                    onClick={handleCertIconClick}
                  />
                </Grid>
                <Grid>
                  <Button
                    className={styles.download}
                    onClick={handleCertIconClick}
                  >
                    Download
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <IconComponent
                style={{ color: completionStatus.color, fontSize: '40px' }}
              />
            )}
          </Grid>
          <Grid className={styles.message}>
            <Typography className={styles.messageHeader}>
              {completionStatus.message}
            </Typography>
            <br />
            {assignedPoints > 0 && (
              <Typography className={styles.messageContent} display="block">
                Score:
                {`${userPoints ?? 0}/${assignedPoints} (${numOfpercentage}%)`}
              </Typography>
            )}
            <Typography className={styles.messageContent} display="block">
              {COMPLETION_OPTIONS}
            </Typography>
          </Grid>
        </Grid>

        <Grid className={styles.action}>
          <CustomBreadcrumbs
            separator={<img src={Separator} alt="Divider" />}
            aria-label="breadcrumb"
          >
            <Button
              onClick={props.handleReturnToLesson}
              className={styles.messageContent}
              style={{
                color: completionStatus.color
              }}
              autoFocus
            >
              Return to Lesson
            </Button>
            {isExternalLesson && (
              <Button
                onClick={props.handleReturnToCourse}
                className={styles.messageContent}
                style={{
                  color: completionStatus.color
                }}
                autoFocus
              >
                Close lesson
              </Button>
            )}
            {isCompleted && !isExternalLesson && (
              <Button
                onClick={props.handleReturnToCourse}
                className={styles.messageContent}
                style={{
                  color: completionStatus.color
                }}
                autoFocus
              >
                Return to Course
              </Button>
            )}
            {showContinue && !isExternalLesson && (
              <Button
                onClick={props.handleContinue}
                className={styles.messageContent}
                style={{
                  color: completionStatus.color
                }}
              >
                Continue to next lesson
              </Button>
            )}
          </CustomBreadcrumbs>
        </Grid>
        {fetchingCert && (
          <Overlay spinner message="Downloading certificate..." />
        )}
        {fetchCertError && (
          <AlertDialog
            title="View Certificate"
            message="Failed to download the certificate."
            onClose={() => setFetchCertError(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
